import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    'raft-wars': ['raft-wars-2'],
    'raft-wars-2': ['raft-wars'],
    'stickman-fighter-epic-battle': ['stickman-fighter-epic-battle-2'],
    'stickman-fighter-epic-battle-2': ['stickman-fighter-epic-battle'],
    'fnaf1': ['fnaf2', 'fnaf3', 'fnaf4', 'fnafsl'],
    'fnaf2': ['fnaf1', 'fnaf3', 'fnaf4', 'fnafsl'],
    'fnaf3': ['fnaf1', 'fnaf2', 'fnaf4', 'fnafsl'],
    'fnaf4': ['fnaf1', 'fnaf2', 'fnaf3', 'fnafsl'],
    'fnafsl': ['fnaf1', 'fnaf2', 'fnaf3', 'fnaf4'],
    'moto-x3m': ['moto-x3m-2', 'moto-x3m-winter', 'moto-x3m-4-winter', 'moto-x3m-5-pool-party', 'moto-x3m-pool-party', 'moto-x3m-spooky-land', 'motox3m-2', 'motox3m-3'],
    'moto-x3m-2': ['moto-x3m', 'moto-x3m-winter', 'moto-x3m-4-winter', 'motox3m-3'],
    'motox3m-2': ['moto-x3m', 'moto-x3m-2', 'motox3m-3'],
    'motox3m-3': ['moto-x3m', 'moto-x3m-2', 'motox3m-2'],
    'granny1': ['granny-2', 'granny-3'],
    'granny-2': ['granny1', 'granny-3'],
    'granny-3': ['granny1', 'granny-2'],
    'run-2': ['run-3'],
    'run-3': ['run-2'],
    'slope-game': ['slope2', 'slope3', 'slope-3', 'slope-racing-3d'],
    'slope2': ['slope-game', 'slope3', 'slope-3'],
    'slope3': ['slope-game', 'slope2', 'slope-3'],
    'slope-3': ['slope-game', 'slope2', 'slope3'],
    'worlds-hardest-game': ['worlds-hardest-game-2', 'worlds-hardest-game-3'],
    'worlds-hardest-game-2': ['worlds-hardest-game', 'worlds-hardest-game-3'],
    'worlds-hardest-game-3': ['worlds-hardest-game', 'worlds-hardest-game-2'],
    'fireboy-and-watergirl': ['fireboy-and-watergirl-1', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-1': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4'],
    'fireboy-and-watergirl-2': ['fireboy-and-watergirl-1', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4'],
    'fireboy-and-watergirl-3': ['fireboy-and-watergirl-2', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5'],
    'fireboy-and-watergirl-4': ['fireboy-and-watergirl-3', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-5': ['fireboy-and-watergirl-4', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-6': ['fireboy-and-watergirl-5', 'fireboy-and-watergirl-4']
  };

  const series1 = seriesMap[slug1] || [];
  return series1.includes(slug2);
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get remaining category games (excluding series games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug)
    );

    // PRIORITY 3: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
